// admin-panel/src/pages/Testimonials.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { testimonialsApi } from '../lib/api';
import { Plus, Edit2, Trash2, Save, X, Star } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = { client_name:'', client_title:'', client_company:'', content:'', rating:5, is_active:true, is_featured:false };

export default function Testimonials() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const { data, isLoading } = useQuery({ queryKey:['admin-testimonials'], queryFn:()=>testimonialsApi.list({per_page:50}) });
  const list = data?.data || data || [];

  const saveMut = useMutation({
    mutationFn: (d) => modal?.id ? testimonialsApi.update(modal.id,d) : testimonialsApi.create(d),
    onSuccess: () => { qc.invalidateQueries(['admin-testimonials']); setModal(null); toast.success('Saved'); },
    onError: (e) => toast.error(e.message||'Failed'),
  });
  const delMut = useMutation({
    mutationFn: testimonialsApi.delete,
    onSuccess: () => { qc.invalidateQueries(['admin-testimonials']); toast.success('Deleted'); },
  });

  function openEdit(t) {
    setForm({ client_name:t.client_name||'', client_title:t.client_title||'', client_company:t.client_company||'', content:t.content||'', rating:t.rating||5, is_active:t.is_active??true, is_featured:t.is_featured??false });
    setModal(t);
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Testimonials</h1>
        <button onClick={()=>{setForm(EMPTY);setModal('new');}} className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90">
          <Plus size={16}/> Add Testimonial
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? Array(6).fill(0).map((_,i)=><div key={i} className="h-40 rounded-xl bg-white/5 animate-pulse"/>) :
        list.map(t => (
          <div key={t.id} className="bg-[#151515] border border-white/5 rounded-xl p-5 group hover:border-[#D4AF37]/20 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="flex gap-0.5">
                {Array(5).fill(0).map((_,i)=><Star key={i} size={13} className={i<t.rating?'text-[#D4AF37]':'text-[#333]'} fill={i<t.rating?'#D4AF37':'none'}/>)}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={()=>openEdit(t)} className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white"><Edit2 size={13}/></button>
                <button onClick={()=>{if(window.confirm('Delete?'))delMut.mutate(t.id)}} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#888] hover:text-red-400"><Trash2 size={13}/></button>
              </div>
            </div>
            <p className="text-[#CCCCCC] text-sm italic mb-3 line-clamp-3">"{t.content}"</p>
            <div>
              <div className="text-white text-sm font-semibold">{t.client_name}</div>
              <div className="text-[#555] text-xs">{t.client_title}{t.client_company&&` · ${t.client_company}`}</div>
            </div>
            <div className="flex gap-2 mt-2">
              {t.is_featured && <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">Featured</span>}
              <span className={`text-xs px-2 py-0.5 rounded-full ${t.is_active?'bg-green-500/10 text-green-400':'bg-gray-500/10 text-gray-500'}`}>{t.is_active?'Active':'Inactive'}</span>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold">{modal?.id?'Edit':'New'} Testimonial</h3>
              <button onClick={()=>setModal(null)}><X size={18} className="text-[#888] hover:text-white"/></button>
            </div>
            <div className="space-y-3">
              {[{k:'client_name',l:'Client Name *',p:'Mr. Sharma'},{k:'client_title',l:'Title',p:'Homeowner'},{k:'client_company',l:'Company/Location',p:'Surat'}].map(({k,l,p})=>(
                <div key={k}>
                  <label className="block text-xs text-[#555] mb-1.5">{l}</label>
                  <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={p} className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"/>
                </div>
              ))}
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Review *</label>
                <textarea value={form.content} onChange={e=>set('content',e.target.value)} rows={4} placeholder="Client review…" className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none"/>
              </div>
              <div>
                <label className="block text-xs text-[#555] mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(r=>(
                    <button key={r} onClick={()=>set('rating',r)} className="p-1">
                      <Star size={20} className={r<=form.rating?'text-[#D4AF37]':'text-[#333]'} fill={r<=form.rating?'#D4AF37':'none'}/>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-4">
                {[{k:'is_active',l:'Active'},{k:'is_featured',l:'Featured'}].map(({k,l})=>(
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <div onClick={()=>set(k,!form[k])} className={`w-9 h-5 rounded-full relative transition-all ${form[k]?'bg-[#D4AF37]':'bg-white/10'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form[k]?'left-4':'left-0.5'}`}/>
                    </div>
                    <span className="text-sm text-[#888]">{l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={()=>setModal(null)} className="flex-1 py-2.5 rounded-xl text-sm text-[#888] bg-white/5 hover:bg-white/10">Cancel</button>
              <button onClick={()=>saveMut.mutate(form)} disabled={saveMut.isPending||!form.client_name||!form.content}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50">
                <Save size={14}/>{saveMut.isPending?'Saving…':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
